import { computed, inject, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, of, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { EntityTypeExtend } from '@data-access/submissions';
import { ORGANIZATION_CONTACT_INITIAL_SIGNAL_STATE } from '@data-access/organizations/constants';
import { OrganizationsV2Service } from '@data-access/organizations/services';
import { withOrganizationContactEntities } from './entities';
import {
  OrganizationContactData,
  OrganizationContactExportArgs,
  OrganizationContactSetupContextOptions,
  OrganizationContactSignalState,
} from '@data-access/organizations/types';
import {
  DIALOG_TYPE,
  OpenDialogState,
  withDialogFeatures,
} from '@data-access/shared';

import {
  buildOrganizationLookupStateFromOrganizations,
  createPendingOrganizationLookupState,
  extractOrganizationIds,
  enrichContactsWithOrganizationName,
  toMutationContactEntity,
  enrichContactWithOrganizationName,
} from '../../utils/organization-contact.utils';

import environment from '@environments/environment';

export const ORGANIZATION_CONTACT_SIGNAL_STORE = signalStore(
  withState(() => inject(ORGANIZATION_CONTACT_INITIAL_SIGNAL_STATE)),
  withProps(
    (store, _organizationsV2Service = inject(OrganizationsV2Service)) => ({
      _organizationsV2Service,
      store,
    }),
  ),
  withFeature(() => withOrganizationContactEntities()),
  withFeature(() => withDialogFeatures()),
  withComputed(({ store, _organizationContactEntities }) => ({
    $organizationcontacts: computed(() => {
      const contacts = _organizationContactEntities();
      if (!contacts) {
        return [];
      }
      return contacts;
    }),
    $entityType: computed(() => store.entityType()),
    $organizationRoles: computed(() => store.organizationRoles()),
    $organizationNameById: computed(() => store.organizationNameById()),
    $organizationIrbAffiliatedById: computed(() =>
      store.organizationIrbAffiliatedById(),
    ),
    $canCloseDialog: computed(() => store.canCloseDialog()),
    $loading: computed(() => store.loading()),
    $isRequestInProgress: computed(() => store.isRequestInProgress()),
    $error: computed(() => store.error()),
    $exportUrl: computed(() => store.exportUrl()),
    $exportArgs: computed(() => store.exportArgs()),
    $searchText: computed(() => store.searchText()),
  })),

  withMethods(
    ({ _organizationsV2Service, _organizationContactEntities, ...store }) => {
      const _buildSearchUrl = (searchText: string) =>
        searchText
          ? `${store.readUrl()}?query=${encodeURIComponent(searchText)}`
          : store.readUrl();

      const _fetchAndPatchOrganizationNames = (organizationIds: string[]) => {
        const currentOrganizationLookupState = {
          organizationNameById: store.organizationNameById(),
          organizationWcgIdById: store.organizationWcgIdById(),
          organizationIrbAffiliatedById: store.organizationIrbAffiliatedById(),
        };

        const netNewOrganizationIds = [
          ...new Set(
            organizationIds
              .filter(Boolean)
              .filter(
                (id) =>
                  !(id in currentOrganizationLookupState.organizationNameById),
              ),
          ),
        ];

        if (netNewOrganizationIds.length === 0) {
          return of(currentOrganizationLookupState);
        }

        const pendingOrganizationLookupState =
          createPendingOrganizationLookupState(
            netNewOrganizationIds,
            currentOrganizationLookupState,
          );

        patchState(store, pendingOrganizationLookupState);

        return _organizationsV2Service
          .listOrganizations({
            organization_keys: netNewOrganizationIds,
            page: 1,
            size: netNewOrganizationIds.length,
          })
          .pipe(
            map((response) =>
              buildOrganizationLookupStateFromOrganizations(
                response.data?.results ?? [],
                Object.fromEntries(
                  netNewOrganizationIds.map((id) => [id, null]),
                ),
              ),
            ),
            map((fetchedOrganizationLookupState) => {
              const mergedOrganizationLookupState = {
                organizationNameById: {
                  ...store.organizationNameById(),
                  ...fetchedOrganizationLookupState.organizationNameById,
                },
                organizationWcgIdById: {
                  ...store.organizationWcgIdById(),
                  ...fetchedOrganizationLookupState.organizationWcgIdById,
                },
                organizationIrbAffiliatedById: {
                  ...store.organizationIrbAffiliatedById(),
                  ...fetchedOrganizationLookupState.organizationIrbAffiliatedById,
                },
              };

              patchState(store, mergedOrganizationLookupState);

              return mergedOrganizationLookupState;
            }),
          );
      };

      const _setupContext = (
        entityId: string,
        entityType: EntityTypeExtend,
        options: OrganizationContactSetupContextOptions = {},
      ) => {
        const { relationshipId, studyId } = options;
        let readUrl = '';
        let writeUrl = '';
        let rolesUrl = '';
        let exportUrl = '';
        let exportArgs: OrganizationContactExportArgs = {};
        switch (entityType) {
          case 'study':
            readUrl = `${environment.irisApi}/${entityType}/${entityId}/organization-affiliation/`;
            writeUrl = `${environment.irisApi}/${entityType}/organization-affiliation/`;
            rolesUrl = `${environment.irisApi}/organization-roles/?entity_type=${entityType}`;
            exportUrl = `${environment.irisApi}/study/${entityId}/organization-contacts/`;
            // sort_by_field and sort_by_order are required query params for the export API.
            exportArgs = { sort_by_field: 'name', sort_by_order: 'asc' };
            break;
          case 'site': {
            // Export endpoint lives under the parent study; site is passed as a query param.
            const exportStudyId = studyId ?? '';
            readUrl = `${environment.irisApi}/${entityType}/${entityId}/organization-affiliation/`;
            writeUrl = `${environment.irisApi}/${entityType}/organization-affiliation/`;
            rolesUrl = `${environment.irisApi}/organization-roles/?entity_type=${entityType}`;
            exportUrl = `${environment.irisApi}/study/${exportStudyId}/organization-contacts/`;
            // site_id, sort_by_field, and sort_by_order are required query params for the export API.
            exportArgs = {
              site_id: entityId,
              sort_by_field: 'name',
              sort_by_order: 'asc',
            };
            break;
          }
          case 'workspace':
            readUrl = `${environment.irisApi}/v2/workspace/${entityId}/organization-affiliation/`;
            rolesUrl = `${environment.irisApi}/v2/workspace/${entityId}/organization-roles/`;
            writeUrl = relationshipId
              ? `${environment.irisApi}/v2/workspace/${entityId}/organization-affiliation/${relationshipId}/`
              : `${environment.irisApi}/v2/workspace/${entityId}/organization-affiliation/`;
            break;
          default:
            throw new Error(
              `Unsupported organization contact entity type: ${String(entityType)}`,
            );
        }
        patchState(store, {
          readUrl,
          writeUrl,
          rolesUrl,
          exportUrl,
          exportArgs,
          entityId,
          entityType,
        });
      };

      const _loadOrganizationRoles = rxMethod<void>(
        pipe(
          tap(() => patchState(store, { error: null, loading: true })),
          switchMap(() =>
            _organizationsV2Service
              .loadOrganizationRoles(store.rolesUrl())
              .pipe(
                tapResponse({
                  complete: () => patchState(store, { loading: false }),
                  error: (error: Error) =>
                    patchState(store, { error, loading: false }),
                  next: (data) => {
                    if (data) {
                      patchState(store, { organizationRoles: data });
                    }
                  },
                }),
              ),
          ),
        ),
      );

      const _loadOrganizationContact = rxMethod<string>(
        pipe(
          tap(() => patchState(store, { error: null, loading: true })),
          switchMap((url) =>
            _organizationsV2Service.loadOrganizationContact(url).pipe(
              switchMap((contacts) => {
                const organizationIds = extractOrganizationIds(contacts);

                return _fetchAndPatchOrganizationNames(organizationIds).pipe(
                  map(
                    ({
                      organizationNameById,
                      organizationWcgIdById,
                      organizationIrbAffiliatedById,
                    }) => ({
                      contacts,
                      organizationNameById,
                      organizationWcgIdById,
                      organizationIrbAffiliatedById,
                    }),
                  ),
                );
              }),
              tapResponse({
                complete: () => patchState(store, { loading: false }),
                error: (error: Error) =>
                  patchState(store, { error, loading: false }),
                next: ({
                  contacts,
                  organizationNameById,
                  organizationWcgIdById,
                  organizationIrbAffiliatedById,
                }) => {
                  if (contacts) {
                    store.loadOrganizationContactEntities(
                      enrichContactsWithOrganizationName(
                        contacts,
                        organizationNameById,
                        organizationWcgIdById,
                        organizationIrbAffiliatedById,
                      ),
                    );
                  }
                },
              }),
            ),
          ),
        ),
      );

      const _addOrganizationContact = rxMethod<OrganizationContactData>(
        pipe(
          tap(() =>
            patchState(store, { error: null, isRequestInProgress: true }),
          ),
          switchMap((payload) =>
            _organizationsV2Service
              .addOrganizationContact(store.readUrl(), payload)
              .pipe(
                switchMap((data) => {
                  const normalizedContact = toMutationContactEntity(
                    data,
                    payload,
                    `new-${payload.organization_id}-${Date.now()}`,
                  );

                  const organizationIdFromResponse =
                    typeof normalizedContact.organization_id === 'string'
                      ? normalizedContact.organization_id
                      : '';

                  return _fetchAndPatchOrganizationNames([
                    organizationIdFromResponse || payload.organization_id,
                  ]).pipe(
                    map(
                      ({
                        organizationNameById,
                        organizationWcgIdById,
                        organizationIrbAffiliatedById,
                      }) => ({
                        data: normalizedContact,
                        organizationNameById,
                        organizationWcgIdById,
                        organizationIrbAffiliatedById,
                      }),
                    ),
                  );
                }),
                tapResponse({
                  complete: () =>
                    patchState(store, { isRequestInProgress: false }),
                  error: (error: Error) =>
                    patchState(store, { error, isRequestInProgress: false }),
                  next: ({ data }) => {
                    if (data) {
                      patchState(store, { canCloseDialog: true });
                      _loadOrganizationContact(
                        _buildSearchUrl(store.searchText()),
                      );
                    }
                  },
                }),
              ),
          ),
        ),
      );

      const _updateOrganizationContact = rxMethod<{
        id: string;
        payload: OrganizationContactData;
      }>(
        pipe(
          tap(() =>
            patchState(store, { error: null, isRequestInProgress: true }),
          ),
          switchMap(({ id, payload }) =>
            _organizationsV2Service
              .updateOrganizationContact(`${store.writeUrl()}${id}/`, payload)
              .pipe(
                switchMap((data) => {
                  const normalizedContact = toMutationContactEntity(
                    data,
                    payload,
                    id,
                  );

                  const organizationIdFromResponse =
                    typeof normalizedContact.organization_id === 'string'
                      ? normalizedContact.organization_id
                      : '';

                  return _fetchAndPatchOrganizationNames([
                    organizationIdFromResponse || payload.organization_id,
                  ]).pipe(
                    map(
                      ({
                        organizationNameById,
                        organizationWcgIdById,
                        organizationIrbAffiliatedById,
                      }) => ({
                        data: normalizedContact,
                        organizationNameById,
                        organizationWcgIdById,
                        organizationIrbAffiliatedById,
                      }),
                    ),
                  );
                }),
                tapResponse({
                  complete: () =>
                    patchState(store, { isRequestInProgress: false }),
                  error: (error: Error) =>
                    patchState(store, { error, isRequestInProgress: false }),
                  next: ({
                    data,
                    organizationNameById,
                    organizationWcgIdById,
                    organizationIrbAffiliatedById,
                  }) => {
                    if (data) {
                      patchState(store, { canCloseDialog: true });
                      store.updateOrganizationContactEntity(
                        id,
                        enrichContactWithOrganizationName(
                          data,
                          organizationNameById,
                          organizationWcgIdById,
                          organizationIrbAffiliatedById,
                        ),
                      );
                    }
                  },
                }),
              ),
          ),
        ),
      );

      const _removeOrganizationContact = rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, { error: null, isRequestInProgress: true }),
          ),
          switchMap((id) =>
            _organizationsV2Service
              .removeOrganizationContact(`${store.writeUrl()}${id}/`)
              .pipe(
                tapResponse({
                  complete: () =>
                    patchState(store, { isRequestInProgress: false }),
                  error: (error: Error) =>
                    patchState(store, { error, isRequestInProgress: false }),
                  next: (_data) => {
                    patchState(store, { canCloseDialog: true });
                    store.removeOrganizationContactEntitiesByRelationshipId(id);
                  },
                }),
              ),
          ),
        ),
      );

      const _openOrganizationContactDialog = (
        dialogMetaData: OpenDialogState,
      ) => {
        store.openDialog({
          component: () => dialogMetaData.component(),
          componentData: dialogMetaData.componentData,
          type: DIALOG_TYPE.GroupFields,
        });
      };

      return {
        setupContext: (
          entityId: string,
          EntityTypeExtend: EntityTypeExtend,
          options?: OrganizationContactSetupContextOptions,
        ) => _setupContext(entityId, EntityTypeExtend, options),

        loadOrganizationRoles: () => _loadOrganizationRoles(),

        loadOrganizationContact: () =>
          _loadOrganizationContact(store.readUrl()),

        addOrganizationContact: (payload: OrganizationContactData) =>
          _addOrganizationContact(payload),

        updateOrganizationContact: (
          id: string,
          payload: OrganizationContactData,
        ) => _updateOrganizationContact({ id, payload }),

        removeOrganizationContact: (id: string) =>
          _removeOrganizationContact(id),

        openOrganizationContactDialog: (dialogMetaData: OpenDialogState) => {
          patchState(store, { canCloseDialog: false });
          _openOrganizationContactDialog(dialogMetaData);
        },

        searchOrganizationContact: (searchText: string) => {
          patchState(store, { searchText });
          _loadOrganizationContact(_buildSearchUrl(searchText));
        },
      };
    },
  ),
) satisfies Type<OrganizationContactSignalState>;
